import { useMemo, useState, useRef, useEffect } from 'react';
import {
  CalendarClock,
  DollarSign,
  Hash,
  Bell,
  Plus,
  Trash2,
  X,
  Check,
} from 'lucide-react';
import useStore from '../../store/useStore';
import { Card, KPICard } from '../../components/ui/Card';
import { Table, Thead, Th, Td, Tr } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { PageHeader } from '../../components/ui/PageHeader';
import { formatCurrency } from '../../utils/format';

// ─── Helpers ───────────────────────────────────────────────────────────────
const normalizeFreq = (f) => {
  if (typeof f === 'number') return f;
  if (f === 'Monthly') return 12;
  if (f === 'Annual') return 1;
  return parseInt(f, 10) || 12;
};

const freqLabel = (f) => (normalizeFreq(f) >= 12 ? 'Monthly' : 'Annual');

/** Advance a payment date forward until it's >= today */
function advanceToNext(dateStr, frequency) {
  if (!dateStr) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  let d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
  d.setHours(0, 0, 0, 0);
  const freq = normalizeFreq(frequency);
  if (freq >= 12) {
    while (d < now) d.setMonth(d.getMonth() + 1);
  } else {
    while (d < now) d.setFullYear(d.getFullYear() + 1);
  }
  return d;
}

function fmtDate(d) {
  if (!d) return '—';
  const date = d instanceof Date ? d : new Date(d);
  return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
}

// ─── Inline-editable cell ──────────────────────────────────────────────────
function EditableCell({ value, displayValue, align = 'right', onSave }) {
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.select();
  }, [editing]);

  const save = () => {
    const num = Number(editVal);
    if (!isNaN(num) && num !== value) onSave(num);
    setEditing(false);
  };

  if (editing) {
    return (
      <Td align={align}>
        <input
          ref={inputRef}
          type="number"
          step="0.01"
          value={editVal}
          onChange={(e) => setEditVal(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.target.blur();
            if (e.key === 'Escape') { setEditing(false); setEditVal(value); }
          }}
          className="w-20 bg-bg-tertiary border border-accent rounded px-2 py-1 text-sm text-text-primary font-mono text-right focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          autoFocus
        />
      </Td>
    );
  }

  return (
    <Td
      align={align}
      className="font-mono tabular-nums cursor-pointer hover:bg-accent/10 transition-colors"
      onClick={() => { setEditing(true); setEditVal(value); }}
      title="Click to edit"
    >
      {displayValue}
    </Td>
  );
}

// ─── Inline-editable text cell ─────────────────────────────────────────────
function EditableTextCell({ value, className = '', onSave }) {
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.select();
  }, [editing]);

  const save = () => {
    if (editVal !== value) onSave(editVal);
    setEditing(false);
  };

  if (editing) {
    return (
      <Td>
        <input
          ref={inputRef}
          type="text"
          value={editVal}
          onChange={(e) => setEditVal(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.target.blur();
            if (e.key === 'Escape') { setEditing(false); setEditVal(value || ''); }
          }}
          className="w-full bg-bg-tertiary border border-accent rounded px-2 py-1 text-sm text-text-primary focus:outline-none"
          autoFocus
        />
      </Td>
    );
  }

  return (
    <Td
      className={`cursor-pointer hover:bg-accent/10 transition-colors ${className}`}
      onClick={() => { setEditing(true); setEditVal(value || ''); }}
      title="Click to edit"
    >
      {value || ''}
    </Td>
  );
}

// ─── Inline-editable date cell ─────────────────────────────────────────────
function EditableDateCell({ value, displayValue, className = '', onSave }) {
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const save = () => {
    if (editVal !== value) onSave(editVal || null);
    setEditing(false);
  };

  if (editing) {
    return (
      <Td>
        <input
          ref={inputRef}
          type="date"
          value={editVal}
          onChange={(e) => setEditVal(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.target.blur();
            if (e.key === 'Escape') { setEditing(false); setEditVal(value || ''); }
          }}
          className="bg-bg-tertiary border border-accent rounded px-2 py-1 text-sm text-text-primary focus:outline-none"
          autoFocus
        />
      </Td>
    );
  }

  return (
    <Td
      className={`cursor-pointer hover:bg-accent/10 transition-colors ${className}`}
      onClick={() => { setEditing(true); setEditVal(value || ''); }}
      title="Click to edit"
    >
      {displayValue}
    </Td>
  );
}

// ─── Add-row blank ─────────────────────────────────────────────────────────
const EMPTY_SUB = {
  billingSource: '',
  company: '',
  item: '',
  cost: '',
  frequency: 12,
  card: '',
  nextPayment: '',
  note: '',
};

// ─── Main Component ────────────────────────────────────────────────────────
export default function Subscriptions() {
  const subscriptions = useStore((s) => s.subscriptions);
  const addSubscription = useStore((s) => s.addSubscription);
  const updateSubscription = useStore((s) => s.updateSubscription);
  const removeSubscription = useStore((s) => s.removeSubscription);

  const [activeCompany, setActiveCompany] = useState('All');
  const [sortField, setSortField] = useState('company');
  const [sortAsc, setSortAsc] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newSub, setNewSub] = useState(EMPTY_SUB);

  // Normalize frequency for all subs
  const normalized = useMemo(
    () => subscriptions.map((s) => ({ ...s, frequency: normalizeFreq(s.frequency) })),
    [subscriptions],
  );

  const companies = useMemo(() => {
    const cs = [...new Set(normalized.map((s) => s.company))].sort();
    return ['All', ...cs];
  }, [normalized]);

  const {
    monthlyCost,
    annualCost,
    count,
    upcomingRenewals,
    filteredAndSorted,
  } = useMemo(() => {
    const annualCost = normalized.reduce((sum, s) => sum + s.cost * s.frequency, 0);
    const monthlyCost = annualCost / 12;
    const count = normalized.length;

    const now = new Date();
    const in30Days = new Date(now);
    in30Days.setDate(in30Days.getDate() + 30);
    const upcomingRenewals = normalized.filter((s) => {
      const next = advanceToNext(s.nextPayment, s.frequency);
      return next && next >= now && next <= in30Days;
    }).length;

    let filtered =
      activeCompany === 'All'
        ? [...normalized]
        : normalized.filter((s) => s.company === activeCompany);

    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortAsc ? -1 : 1;
      if (aVal > bVal) return sortAsc ? 1 : -1;
      return 0;
    });

    return { monthlyCost, annualCost, count, upcomingRenewals, filteredAndSorted: filtered };
  }, [normalized, activeCompany, sortField, sortAsc]);

  const handleSort = (field) => {
    if (sortField === field) setSortAsc(!sortAsc);
    else { setSortField(field); setSortAsc(true); }
  };

  const handleAddSubmit = () => {
    if (!newSub.billingSource && !newSub.company) return;
    const cost = parseFloat(newSub.cost);
    if (isNaN(cost)) return;
    const freq = normalizeFreq(newSub.frequency);
    addSubscription({
      ...newSub,
      cost,
      frequency: freq,
      nextPayment: newSub.nextPayment || null,
    });
    setNewSub(EMPTY_SUB);
    setShowAdd(false);
  };

  const handleFieldSave = (id, field, value) => {
    const sub = normalized.find((s) => s.id === id);
    if (!sub) return;
    const updates = { [field]: value };
    if (field === 'frequency') updates.frequency = normalizeFreq(value);
    updateSubscription(id, updates);
  };

  const SortHeader = ({ field, children, align = 'left' }) => (
    <Th align={align}>
      <button
        className="inline-flex items-center gap-1 hover:text-text-primary transition-colors"
        onClick={() => handleSort(field)}
      >
        {children}
        {sortField === field && (
          <span className="text-accent text-[10px]">{sortAsc ? '\u25B2' : '\u25BC'}</span>
        )}
      </button>
    </Th>
  );

  const inputClass =
    'w-full bg-bg-tertiary border border-border rounded px-2 py-1 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent';
  const numInputClass =
    `${inputClass} text-right font-mono [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`;
  const dateInputClass =
    `${inputClass} [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-50`;

  return (
    <div>
      <PageHeader title="Subscriptions" subtitle={`${count} active subscriptions`}>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-accent text-white hover:bg-accent/90 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Add
        </button>
      </PageHeader>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 kpi-grid">
        <KPICard title="Monthly Cost" value={formatCurrency(monthlyCost, 2)} icon={DollarSign} />
        <KPICard title="Annual Cost" value={formatCurrency(annualCost, 2)} icon={CalendarClock} />
        <KPICard title="Active Subscriptions" value={count} icon={Hash} />
        <KPICard title="Upcoming Renewals (30d)" value={upcomingRenewals} icon={Bell} />
      </div>

      {/* Company Filter */}
      <div className="flex flex-wrap gap-2 mb-4">
        {companies.map((c) => (
          <button
            key={c}
            onClick={() => setActiveCompany(c)}
            className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
              activeCompany === c
                ? 'bg-accent text-white'
                : 'bg-bg-tertiary text-text-secondary hover:text-text-primary'
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Table */}
      <Card>
        <div className="overflow-x-auto">
          <Table className="min-w-[1100px]">
            <Thead>
              <tr>
                <SortHeader field="billingSource">Billing Source</SortHeader>
                <SortHeader field="company">Company</SortHeader>
                <SortHeader field="item">Item</SortHeader>
                <SortHeader field="cost" align="right">Cost</SortHeader>
                <SortHeader field="frequency" align="right">Freq</SortHeader>
                <Th align="right">Annual Cost</Th>
                <Th>Type</Th>
                <SortHeader field="nextPayment">Next Payment</SortHeader>
                <SortHeader field="card">Card</SortHeader>
                <Th>Note</Th>
                <Th align="center">×</Th>
              </tr>
            </Thead>
            <tbody>
              {/* Inline Add Row */}
              {showAdd && (
                <tr className="border-b border-accent/30 bg-accent/5">
                  <Td>
                    <input type="text" placeholder="Source" value={newSub.billingSource}
                      onChange={(e) => setNewSub({ ...newSub, billingSource: e.target.value })}
                      className={inputClass} />
                  </Td>
                  <Td>
                    <input type="text" placeholder="Company" value={newSub.company}
                      onChange={(e) => setNewSub({ ...newSub, company: e.target.value })}
                      className={inputClass} />
                  </Td>
                  <Td>
                    <input type="text" placeholder="Item" value={newSub.item}
                      onChange={(e) => setNewSub({ ...newSub, item: e.target.value })}
                      className={inputClass} />
                  </Td>
                  <Td>
                    <input type="number" placeholder="0.00" step="0.01" value={newSub.cost}
                      onChange={(e) => setNewSub({ ...newSub, cost: e.target.value })}
                      className={numInputClass} />
                  </Td>
                  <Td>
                    <input type="number" placeholder="12" value={newSub.frequency}
                      onChange={(e) => setNewSub({ ...newSub, frequency: parseInt(e.target.value, 10) || 12 })}
                      className={`${numInputClass} w-16`} />
                  </Td>
                  <Td align="right" className="font-mono text-text-muted">
                    {newSub.cost ? formatCurrency(parseFloat(newSub.cost) * normalizeFreq(newSub.frequency), 2) : '—'}
                  </Td>
                  <Td>
                    <Badge variant={normalizeFreq(newSub.frequency) >= 12 ? 'accent' : 'warning'}>
                      {freqLabel(newSub.frequency)}
                    </Badge>
                  </Td>
                  <Td>
                    <input type="date" value={newSub.nextPayment}
                      onChange={(e) => setNewSub({ ...newSub, nextPayment: e.target.value })}
                      className={dateInputClass} />
                  </Td>
                  <Td>
                    <input type="text" placeholder="Card" value={newSub.card}
                      onChange={(e) => setNewSub({ ...newSub, card: e.target.value })}
                      className={inputClass} />
                  </Td>
                  <Td>
                    <input type="text" placeholder="Note" value={newSub.note}
                      onChange={(e) => setNewSub({ ...newSub, note: e.target.value })}
                      className={inputClass} />
                  </Td>
                  <Td align="center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={handleAddSubmit}
                        className="p-1 rounded hover:bg-success/20 text-success transition-colors" title="Save">
                        <Check className="w-4 h-4" />
                      </button>
                      <button onClick={() => { setShowAdd(false); setNewSub(EMPTY_SUB); }}
                        className="p-1 rounded hover:bg-danger/20 text-danger transition-colors" title="Cancel">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </Td>
                </tr>
              )}

              {filteredAndSorted.map((sub) => {
                const freq = normalizeFreq(sub.frequency);
                const annual = Math.round(sub.cost * freq * 100) / 100;
                const nextDate = advanceToNext(sub.nextPayment, freq);

                return (
                  <Tr key={sub.id}>
                    <EditableTextCell
                      value={sub.billingSource}
                      className="text-text-secondary text-xs"
                      onSave={(v) => updateSubscription(sub.id, { billingSource: v })}
                    />
                    <EditableTextCell
                      value={sub.company}
                      className="text-text-primary font-medium"
                      onSave={(v) => updateSubscription(sub.id, { company: v })}
                    />
                    <EditableTextCell
                      value={sub.item}
                      className="text-text-secondary"
                      onSave={(v) => updateSubscription(sub.id, { item: v })}
                    />
                    <EditableCell
                      value={sub.cost}
                      displayValue={formatCurrency(sub.cost, 2)}
                      onSave={(v) => handleFieldSave(sub.id, 'cost', v)}
                    />
                    <EditableCell
                      value={freq}
                      displayValue={String(freq)}
                      onSave={(v) => handleFieldSave(sub.id, 'frequency', v)}
                    />
                    <Td align="right" className="font-mono tabular-nums text-text-primary">
                      {formatCurrency(annual, 2)}
                    </Td>
                    <Td>
                      <Badge variant={freq >= 12 ? 'accent' : 'warning'}>
                        {freqLabel(freq)}
                      </Badge>
                    </Td>
                    <EditableDateCell
                      value={sub.nextPayment || ''}
                      displayValue={fmtDate(nextDate)}
                      className="text-text-secondary text-xs tabular-nums"
                      onSave={(v) => updateSubscription(sub.id, { nextPayment: v })}
                    />
                    <EditableTextCell
                      value={sub.card}
                      className="text-text-secondary text-xs"
                      onSave={(v) => updateSubscription(sub.id, { card: v })}
                    />
                    <EditableTextCell
                      value={sub.note}
                      className="text-text-muted text-xs max-w-[200px] truncate"
                      onSave={(v) => updateSubscription(sub.id, { note: v })}
                    />
                    <Td align="center">
                      <button
                        onClick={() => removeSubscription(sub.id)}
                        className="p-1 rounded hover:bg-danger/20 text-text-muted hover:text-danger transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
          {filteredAndSorted.length === 0 && (
            <p className="text-text-muted text-sm text-center py-8">
              No subscriptions found.
            </p>
          )}
        </div>
      </Card>
    </div>
  );
}
